/**
 * Web Push — candidato (Fase 4).
 * Ativo com REACT_APP_ENABLE_PWA + REACT_APP_ENABLE_PUSH_NOTIFICATIONS.
 */

import { apiClient } from '../api/apiClient';
import { CANDIDATE_PWA_ENABLED, isCandidatePwaContext } from './candidatePwa';
import { getTenantCandidatePwaContracted } from './tenantPwaContract';

export const CANDIDATE_PUSH_BUILD_ENABLED =
  CANDIDATE_PWA_ENABLED && process.env.REACT_APP_ENABLE_PUSH_NOTIFICATIONS === 'true';

export function isCandidatePushFeatureActive() {
  return CANDIDATE_PUSH_BUILD_ENABLED && getTenantCandidatePwaContracted();
}

const DISMISS_KEY = 'ingressa_push_optin_dismissed';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushOptInDismissed() {
  return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(DISMISS_KEY) === '1';
}

export function dismissPushOptIn() {
  sessionStorage.setItem(DISMISS_KEY, '1');
}

/** Mensagem amigável quando «Ativar avisos» falha (manual do candidato). */
export function pushSubscribeErrorMessage(reason, detail) {
  const r = (reason || '').toString();
  if (r === 'denied') {
    return 'As notificações estão bloqueadas neste celular. Em Configurações → Apps, abra o aplicativo (ou o Chrome) e ative Notificações. Depois tente de novo.';
  }
  if (r === 'default') {
    return 'Toque em Ativar avisos e, quando o celular perguntar, escolha Permitir.';
  }
  if (r === 'context') {
    return 'Abra o Ingressa pelo ícone na tela inicial (app instalado), não pela aba do navegador. Feche o app, abra de novo pelo ícone e tente outra vez.';
  }
  if (r === 'unsupported') {
    return 'Este celular ou navegador não suporta avisos automáticos. Use Chrome atualizado (Android) ou Safari (iPhone).';
  }
  if (r === 'server_disabled' || r === 'disabled') {
    return 'Os avisos automáticos não estão disponíveis nesta instituição no momento. Você ainda verá avisos dentro do app ao abrir a área do candidato.';
  }
  if (r === 'service_worker') {
    return 'O aplicativo ainda está carregando. Feche o app completamente, abra de novo pelo ícone na tela inicial, aguarde a tela carregar e tente Ativar avisos novamente.';
  }
  if (r === 'network' || r === 'subscribe_failed') {
    return 'Não foi possível conectar para ativar os avisos. Confira a internet (Wi‑Fi ou dados) e tente de novo em alguns segundos.';
  }
  if (detail && typeof detail === 'string' && detail.length < 120) {
    return `Não foi possível ativar os avisos. ${detail}`;
  }
  return 'Não foi possível ativar os avisos. Feche o app, abra pelo ícone na tela inicial, confira se as notificações estão permitidas nas configurações do celular e tente de novo.';
}

export async function syncCandidatePushSubscription() {
  if (!isCandidatePushFeatureActive() || !isCandidatePwaContext()) {
    return { ok: false, reason: 'context' };
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'unsupported' };
  }
  if (Notification.permission !== 'granted') {
    return { ok: false, reason: Notification.permission || 'default' };
  }
  try {
    const cfg = await apiClient.getPushConfig();
    if (!cfg?.enabled || !cfg?.publicKey) {
      return { ok: false, reason: 'server_disabled' };
    }
    let reg;
    try {
      reg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error('service_worker_timeout')), 15000);
        }),
      ]);
    } catch {
      return { ok: false, reason: 'service_worker' };
    }
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      await existing.unsubscribe();
    }
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(cfg.publicKey),
    });
    await apiClient.subscribePush(sub.toJSON());
    return { ok: true };
  } catch (err) {
    const msg = (err && err.message) || '';
    if (/fetch|network|failed to fetch/i.test(msg)) {
      return { ok: false, reason: 'network', detail: msg };
    }
    return { ok: false, reason: 'subscribe_failed', detail: msg };
  }
}

export async function subscribeCandidatePush() {
  if (!isCandidatePushFeatureActive()) return { ok: false, reason: 'disabled' };
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'unsupported' };
  }
  try {
    const cfg = await apiClient.getPushConfig();
    if (!cfg?.enabled || !cfg?.publicKey) {
      return { ok: false, reason: 'server_disabled' };
    }
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { ok: false, reason: permission };
    }
    return syncCandidatePushSubscription();
  } catch (err) {
    const msg = (err && err.message) || '';
    if (/fetch|network|failed to fetch/i.test(msg)) {
      return { ok: false, reason: 'network', detail: msg };
    }
    return { ok: false, reason: 'subscribe_failed', detail: msg };
  }
}

export async function getCandidatePushStatus() {
  if (!isCandidatePushFeatureActive()) {
    return { enabled: false, subscribed: false, showOptIn: false };
  }
  try {
    const status = await apiClient.getPushStatus();
    const standalone = isCandidatePwaContext();
    return {
      enabled: !!status.enabled,
      subscribed: !!status.subscribed,
      showOptIn: !!status.enabled && standalone && !status.subscribed && !pushOptInDismissed(),
    };
  } catch {
    return { enabled: false, subscribed: false, showOptIn: false };
  }
}
